<template>
  <div
    v-if="!isKnowledgeAiLinkEnabled && isSupportIconVisible"
    class="support-icon-wrapper"
    :class="[
      getWidgetPositionedStyle,
      {
        'support-icon-wrapper-hide': isWindowMaximized,
        'hide-support-icon-in-fullscreen': isChatWidgetFullScreen
      }
    ]"
  >
    <button
      id="support-icon-container"
      class="tab-focused-btn-with-img support-icon-container"
      :aria-label="getAriaLabelForSupportIconContainer"
      aria-haspopup="true"
      @click="handleSupportClicked"
    >
      <div
        class="support-icon"
        :style="getSupportIconBgStyle"
        :class="[
          getTextPositionedStyle,
          { 'support-icon-mobile': isSupportIconForMobileEnabled }
        ]"
      >
        <div
          v-if="isSupportIconTextAvailable"
          v-t="getsupportIconText"
          class="support-text"
          :style="{
            ...getSupportIconTextStyle,
            ...getFontSizeForChatInterface('supportText')
          }"
          :class="[
            isThemeChange,
            { 'support-text-mobile': isSupportIconForMobileEnabled }
          ]"
        />
        <img
          :src="supportIconUrl"
          alt="support icon"
          :class="[
            isIconWIthText,
            { 'support-icon-image-mobile': isSupportIconForMobileEnabled },
            getSupportIconAnimation
          ]"
          :style="getSupportIconIconStyle"
          class="support-icon-image"
        />
        <div
          v-if="isUnreadIndicatorValid"
          class="unread-indicator-icon-container"
          :style="getUnreadIndicatorStyle"
        >
          <BaseIcon
            name="UnreadIndicatorIcon"
            :height="isDeviceTypeMobile ? '12px' : '16px'"
            :width="isDeviceTypeMobile ? '12px' : '16px'"
          />
        </div>
      </div>
    </button>
  </div>
</template>

<script>
import { eventType, gradient } from '@/services/constants'
import {
  emitEventToParentWindow,
  configureSoundInNotificationService,
  getEventDataPoints
} from '@/services/utils'
import { chatWidgetScreenSizes, status } from '@/services/constants'
import {
  isWidgetDefaultOpened,
  getSupportIconWidthAndHeight,
  getDeviceType,
  getGradient
} from '@/services/utils'
import Storage from '@/services/storageLib'
import BaseIcon from '@/views/BaseIcon.vue'
import fontSizeMixin from '@/mixins/fontSizeMixin'

export default {
  components: {
    BaseIcon
  },
  mixins: [fontSizeMixin],
  data() {
    return {
      supportIconUrl: '',
      widgetPosition: ''
    }
  },
  computed: {
    getAriaLabelForSupportIconContainer() {
      return (
        this.$store.state?.widgetConfig?.supportIconConfig?.ariaLabel || 'chat'
      )
    },
    isSupportIconVisible() {
      return this.$store.state.supportIconState.visible
    },
    theme() {
      return this.getHeaderTheme?.current === 'dark' ? 'light' : 'dark'
    },
    isUnreadIndicatorValid() {
      return this.$store.state.unreadMessagesState.enabled
    },
    isSupportIconForMobileEnabled() {
      return (
        this.$store.state.widgetConfig?.supportIconConfig
          ?.isDynamicIconScalingEnabled &&
        this.isDeviceTypeMobile &&
        !this.$store.getters.isKnowledgeAiEnabled
      )
    },
    isDeviceTypeMobile() {
      return getDeviceType() === 'mobile'
    },
    getWidgetPositionedStyle() {
      return this.widgetPosition === 'left'
        ? 'support-icon-left'
        : 'support-icon-right'
    },
    isDefaultFullScreen() {
      return this.$store.state.widgetConfig.isDefaultFullScreen
    },
    isChatWidgetFullScreen() {
      return this.$store.state.isChatWidgetFullScreen
    },
    isWindowMaximized() {
      return this.$store.state.isWindowMaximized
    },
    supportText() {
      return this.$store.state.widgetConfig.supportText
    },
    supportIcon() {
      // We will migrate the support icons inside supportIconConfig.
      return (
        this.$store.state.widgetConfig?.supportIconConfig?.icon ||
        this.$store.state.widgetConfig?.supportIcon
      )
    },
    getHeaderTheme() {
      return this.$store.state?.widgetConfig?.theme
    },
    getHeaderGradientDirection() {
      return (
        this.getHeaderTheme?.gradientDirection ||
        gradient.gradientDirectionConfig.defaultGradientDirection
      )
    },
    getHeaderGradientColors() {
      return this.getHeaderTheme?.gradientColors || [this.getHeaderSolidColor]
    },
    getHeaderSolidColor() {
      return this.getHeaderTheme?.color || '#000000'
    },
    isGlobalColorThemeEnabled() {
      return this.$store.state.widgetConfig?.theme?.globalColorTheme || false
    },
    getSupportIconBackgroundColor() {
      return (
        this.$store.state.widgetConfig?.supportIconConfig?.backgroundColor ||
        '#000000'
      )
    },
    getSupportIconGradientColors() {
      return this.isGlobalColorThemeEnabled
        ? this.getHeaderGradientColors
        : this.$store.state.widgetConfig?.supportIconConfig?.gradientColors || [
            this.getSupportIconBackgroundColor
          ]
    },
    getSupportIconGradientDirection() {
      return this.isGlobalColorThemeEnabled
        ? this.getHeaderGradientDirection
        : this.$store.state.widgetConfig?.supportIconConfig
            ?.gradientDirection ||
            gradient.gradientDirectionConfig.defaultGradientDirection
    },
    getSupportIconBgStyle() {
      let bgStyle = {}

      const bgColor = this.getSupportIconBackgroundColor
      const gradientBackgroundImage = this.$store.getters.isGradientEnabled
        ? getGradient(
            this.getSupportIconGradientColors,
            this.getSupportIconGradientDirection
          )
        : ''
      if (bgColor) {
        bgStyle = {
          background: bgColor,
          ...(gradientBackgroundImage && {
            backgroundImage: gradientBackgroundImage
          })
        }
      }

      const radius =
        this.$store.state.widgetConfig?.supportIconConfig?.radius || 'medium'
      const radiusConfig = {
        none: '0px',
        small: '4px',
        medium: '8px',
        large: '30px'
      }

      if (radius) {
        bgStyle = {
          ...bgStyle,
          'border-radius': radiusConfig[radius] || '8px'
        }
      }

      return bgStyle
    },
    getUnreadIndicatorStyle() {
      const isIconRadiusLarge =
        this.$store.state.widgetConfig?.supportIconConfig?.radius === 'large'

      if (
        !this.$store.state.widgetConfig?.supportIconConfig?.isSupportTextEnabled
      ) {
        return !this.isDeviceTypeMobile
          ? {
              right: '-8px',
              top: '-14px'
            }
          : !isIconRadiusLarge
            ? {
                right: '-6px',
                top: '-13px'
              }
            : {
                right: '0px',
                top: '-13px'
              }
      } else {
        return this.isDeviceTypeMobile
          ? !isIconRadiusLarge
            ? {
                right: '-6px',
                top: '-13px'
              }
            : {
                right: '0px',
                top: '-13px'
              }
          : !isIconRadiusLarge
            ? {
                right: '-8px',
                top: '-14px'
              }
            : {
                right: '6px',
                top: '-16px'
              }
      }
    },
    getSupportIconTextStyle() {
      const textColor =
        this.$store.state.widgetConfig?.supportIconConfig?.textColor || ''
      if (!textColor) {
        return {}
      }
      return {
        color: textColor
      }
    },
    getSupportIconBorder() {
      return this.$store.state.widgetConfig?.supportIconConfig?.iconBorder || ''
    },
    getSupportIconBoxShadow() {
      return (
        this.$store.state.widgetConfig?.supportIconConfig?.iconBoxShadow || ''
      )
    },
    getSupportIconWidth() {
      return (
        this.$store.state.widgetConfig?.supportIconConfig?.iconSize?.width ||
        'auto'
      )
    },
    getSupportIconHeight() {
      return (
        this.$store.state.widgetConfig?.supportIconConfig?.iconSize?.height ||
        'auto'
      )
    },
    getSupportIconAnimation() {
      return (
        this.$store.state.widgetConfig?.supportIconConfig?.iconAnimation || ''
      )
    },
    getSupportIconIconStyle() {
      let iconPadding =
        this.$store.state.widgetConfig?.supportIconConfig?.iconPadding
      if (iconPadding === undefined) {
        iconPadding =
          this.$store.state.widgetConfig?.supportIconConfig
            ?.isSupportTextEnabled
      }
      return iconPadding
        ? this.isSupportIconForMobileEnabled
          ? {
              'max-width': '20px',
              'max-height': '20px'
            }
          : {}
        : this.isSupportIconForMobileEnabled
          ? {
              margin: '0px',
              padding: '0px',
              width: 'auto',
              height: 'auto',
              'max-width': '20px',
              'max-height': '20px'
            }
          : {
              'max-width': '60px',
              margin: '0px',
              padding: '0px',
              width: this.getSupportIconWidth,
              height: this.getSupportIconHeight,
              border: this.getSupportIconBorder,
              'box-shadow': this.getSupportIconBoxShadow
            }
    },
    widgetStatus() {
      return this.$store.state.widgetStatus
    },
    getTextPositionedStyle() {
      if (!this.isSupportIconTextAvailable) {
        return ''
      }
      return this.getsupportIconPosition === 'left'
        ? 'text-icon-left'
        : 'text-icon-right'
    },
    isIconWIthText() {
      return this.isSupportIconTextAvailable ? 'icon-with-text' : ''
    },
    isThemeChange() {
      return this.theme === 'light' ? 'text-light' : 'text-dark'
    },
    isSupportIconTextAvailable() {
      const val = Boolean(
        this.$store.state.widgetConfig?.supportIconConfig
          ?.isSupportTextEnabled &&
          this.$store.state.widgetConfig?.supportIconConfig?.supportTextValue
      )
      return val
    },
    getsupportIconText() {
      const text =
        this.$store.state.widgetConfig?.supportIconConfig?.supportTextValue
      return text
    },
    getsupportIconPosition() {
      const pos =
        this.$store.state.widgetConfig?.supportIconConfig?.positionOfIcon
      return pos
    },
    getsupportIconRadius() {
      // We will be taking this change in 12.0.0 keeping the util
      const radius =
        this.$store.state.widgetConfig?.supportIconConfig?.radius || 'medium'
      const radiusConfig = {
        none: '0px',
        small: '4px',
        medium: '8px',
        large: '30px'
      }
      return radiusConfig[radius]
    },
    isKnowledgeAiLinkEnabled() {
      return (
        this.$store.getters.isKnowledgeAiEnabled &&
        this.$store.getters.knowledgeAiLaunchButtonData?.type === 'link'
      )
    },
    isEventAnalyticsMP() {
      return this.$store.state.widgetConfig?.eventAnalyticsMP || false
    }
  },

  watch: {
    widgetStatus: {
      handler(updatedWidgetStatus) {
        if (this.isWidgetStateChanged(updatedWidgetStatus)) {
          this.handleSupportClicked()
        } else if (this.isScreenStateChanged(updatedWidgetStatus)) {
          this.handleIFrame()
        }
      },
      deep: true
    },

    isWindowMaximized(value) {
      this.handleIFrame()
      if (value) {
        this.supportIconUrl = `${this.$cdn}/images/cross_${this.theme}.svg`
      } else {
        this.supportIconUrl = this.getSupportIcon()
      }
    },

    theme() {
      this.supportIconUrl = this.getSupportIcon()
    },

    supportIcon() {
      this.supportIconUrl = this.getSupportIcon()
    }
  },

  created() {
    this.supportIconUrl = this.getSupportIcon()
    this.widgetPosition =
      window.parent.document
        .getElementById('netomiChatWindow')
        ?.getAttribute('position') || 'right'
  },

  methods: {
    async handleSupportClicked() {
      this.isEventAnalyticsMP &&
        this.$segment.global.sendEvent('bot_icon_click')
      emitEventToParentWindow(eventType.USER_OPEN_WIDGET, getEventDataPoints())
      const config = this.$store.state?.widgetConfig
      this.$store.dispatch('updateUnreadMessagesState', { enabled: false })
      this.$store.dispatch('toggleChatWindowStatus', !this.isWindowMaximized)
      Storage.setSessionStorageData('userInteracted', true)
      configureSoundInNotificationService(config?.sound)
      if (!isWidgetDefaultOpened()) {
        // Trigger callout bubble greeting if only this is present in widget config
        if (Object.keys(config)?.length > 0)
          await this.$store.dispatch('fetchHistory')
        if (
          !config?.proActiveGreetings &&
          config?.calloutBubble?.calloutBubbleEnabled &&
          config?.calloutBubble?.intent
        ) {
          this.handleCalloutGreetingTrigger()
          return
        } else {
          this.$store.dispatch('triggerProActiveGreetings')
        }
      }
    },
    handleCalloutGreetingTrigger() {
      // Resize CW first and handle send in next tick (repainting issues in chrome)
      emitEventToParentWindow(
        eventType.UPDATE_PARENT_WINDOW_STYLE,
        chatWidgetScreenSizes.LARGE_SCREEN_DEVICES
      )
      let timeoutId = setTimeout(() => {
        this.$store.dispatch('toggleChatWindowStatus', true)
        this.$store.dispatch('triggerCalloutBubbleGreetings')
        clearTimeout(timeoutId)
      }, 0)
      Storage.setSessionStorageData('userInteracted', true)
    },
    handleIFrame() {
      if (this.isWindowMaximized && window.outerWidth <= 767) {
        emitEventToParentWindow(
          eventType.UPDATE_PARENT_WINDOW_STYLE,
          chatWidgetScreenSizes.SMALL_SCREEN_DEVICES
        )
      } else if (this.isWindowMaximized && window.outerWidth >= 768) {
        const isFullScreen =
          this.widgetStatus.isFullScreen === undefined
            ? this.isDefaultFullScreen
            : this.widgetStatus.isFullScreen
        emitEventToParentWindow(
          eventType.UPDATE_PARENT_WINDOW_STYLE,
          isFullScreen
            ? chatWidgetScreenSizes.SMALL_SCREEN_DEVICES
            : chatWidgetScreenSizes.LARGE_SCREEN_DEVICES,
          this.$store.state?.widgetConfig?.chatWidget?.visibility || 'default'
        )
        this.$store.dispatch('updateChatWindowFullScreenState', isFullScreen)
      } else {
        this.$store.dispatch('updateChatWindowFullScreenState', false)
        this.$nextTick(() => {
          emitEventToParentWindow(
            eventType.UPDATE_PARENT_WINDOW_STYLE,
            getSupportIconWidthAndHeight()
          )
        })
      }
      this.$store.dispatch('updateWidgetStatus', this.widgetStatus)
    },
    getSupportIcon() {
      if (this.supportIcon) return this.supportIcon
      else return `${this.$cdn}/images/support_icon_${this.theme}.svg`
    },
    isWidgetStateChanged(currentWidgetState) {
      return !(
        (currentWidgetState.status === status.OPEN && this.isWindowMaximized) ||
        (currentWidgetState.status === status.CLOSE && !this.isWindowMaximized)
      )
    },
    isScreenStateChanged(currrentScreenState) {
      return (
        currrentScreenState.status === status.OPEN &&
        currrentScreenState.isFullScreen !== undefined &&
        currrentScreenState.isFullScreen !== this.isChatWidgetFullScreen
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/css/theme.scss';
@import '@/styles/css/commonStyles.scss';

.support-icon-wrapper {
  @include flex-vertical-center;
  opacity: 1;
  visibility: visible;
  transition: 0.3s all ease-in-out;
  animation: zoom-in-zoom-out 0.3s ease-out;
  transform-origin: center center;
  transition: 0.2 all ease-in;

  &.support-icon-wrapper-hide {
    position: absolute;
    left: -9999 !important;
    top: -9999 !important;
    opacity: 0;
    visibility: hidden;
    transition: 0.3s all ease-in-out;
    transform: scale(0);
    animation: zoom-in-zoom-out 0.3s ease-out;
  }

  .support-icon-container {
    max-width: 100% !important;
    .support-icon {
      @include display-flex;
      @include flex-vertical-horizontal-center;
      border-radius: 16px;
      background-color: var(--widget-primary-color);
      height: 60px;
      cursor: pointer;
      position: relative;
      min-width: 60px;

      &.support-icon-mobile {
        height: 40px;
        max-height: 40px;
        min-width: 40px;
        max-width: 40px;
      }

      .support-icon-image {
        width: 32px;
        height: 32px;
        margin: 14px;
        object-fit: contain;
      }

      &.text-icon-right {
        justify-content: flex-start;
        height: 60px;

        img.icon-with-text {
          margin: 14px 14px 14px 12px;

          &.support-icon-image-mobile {
            margin: 14px 10px 14px 10px;
          }
        }

        .support-text {
          padding: 20px 0 20px 20px;

          &.support-text-mobile {
            padding: 20px 0 20px 10px;
          }
        }
      }

      &.text-icon-left {
        flex-direction: row-reverse;
        height: 60px;

        img.icon-with-text {
          margin: 14px 12px 14px 14px;

          &.support-icon-image-mobile {
            margin: 14px 10px 14px 10px;
          }
        }

        .support-text {
          padding: 20px 20px 20px 0;

          &.support-text-mobile {
            padding: 20px 10px 20px 0;
          }
        }
      }

      .support-text {
        font-weight: 500;
        line-height: 20px;
        word-break: break-word;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &.support-text-mobile {
          font-weight: 400;
        }

        &.text-light {
          color: #fff;
        }

        &.text-dark {
          color: #000;
        }
      }

      .notification {
        position: absolute;
        top: -9px;
        left: -8px;
        @include round(50%);
        background: var(--widget-primary-color);
        border: 1px solid $white-color;
        color: $white-color;
        font-size: 10px;
        font-weight: 500;
        @include flex-vertical-center;
        justify-content: center;
        width: 16px;
        height: 16px;
      }

      .text {
        font-size: 12px;
        font-weight: 600;
        color: $white-color;
        border-left: 1px solid rgba(0, 0, 0, 0.1);
        padding: 0 10px;
        @include flex-vertical-center;

        @media screen and (max-device-width: 767px) {
          display: none;
        }
      }

      .unread-indicator-icon-container {
        width: fit-content;
        height: fit-content;
        margin-top: 8px;
        margin-left: -18px;
        z-index: 1;
        position: absolute;
      }
    }
  }
}

.support-icon-left {
  justify-content: flex-start;

  .support-icon {
    margin: 15px 0 0 10px;
  }
}

.support-icon-right {
  justify-content: flex-end;

  .support-icon {
    margin: 15px 10px 0 10px;
  }
}

.hide-support-icon-in-fullscreen {
  display: none;
}

@media screen and (max-device-width: 767px) {
  .support-icon-wrapper-hide {
    display: none;
  }
}

@keyframes zoom-in-zoom-out {
  0% {
    transform: scale(0);
  }

  100% {
    transform: scale(1);
  }
}

.fade {
  animation: fade 6s infinite;
}

.pulse {
  animation: pulse 5s infinite;
}

@keyframes fade {
  0%,
  100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
</style>
